import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Activity, ArrowRight, Building2, KeyRound } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [operatorCode, setOperatorCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!operatorCode.trim()) {
      toast.error('Please enter operator code');
      return;
    }
    setLoading(true);
    try {
      const operator = await login(operatorCode);
      toast.success(`Welcome ${operator.name}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || 'Operator code not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(180deg, #eaf2ff 0%, #f8fafc 42%, #f8fafc 100%)' }}
    >
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2.5 mb-2">
            <div className="w-10 h-10 bg-[#0F172A] rounded-lg flex items-center justify-center">
              <Activity className="h-5 w-5 text-blue-400" />
            </div>
            <div className="text-left">
              <h1 className="text-xl font-bold text-slate-900 tracking-tight leading-none">Manpower Monitor</h1>
              <p className="text-[10px] text-slate-500 font-medium tracking-widest uppercase">Workforce Tracking System</p>
            </div>
          </div>
          <div className="small text-slate-500">No password login. Enter your operator code from the parent company to continue.</div>
        </div>

        <Card className="border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 pt-5 pb-0">
            <div className="rounded-3 px-3 py-2 d-inline-flex align-items-center gap-2 small fw-semibold text-primary" style={{ background: '#eff6ff' }}>
              <Building2 size={15} /> Parent Company Operator Mode
            </div>
          </div>
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-bold text-slate-900">Enter Operator Code</CardTitle>
            <CardDescription className="text-sm text-slate-500">The app checks `recruit_operators` in Supabase and remembers your code on this browser.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="operator-code" className="text-sm font-medium text-slate-700">Operator Code</Label>
                <Input
                  id="operator-code"
                  type="text"
                  value={operatorCode}
                  onChange={(e) => setOperatorCode(e.target.value.toUpperCase())}
                  placeholder="M001"
                  data-testid="operator-code-input"
                  className="h-10"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="operator-code-submit"
                className="w-full h-10 bg-[#0F172A] hover:bg-slate-800"
              >
                <KeyRound className="h-4 w-4 mr-2" />
                {loading ? 'Checking code...' : 'Continue'}
                {!loading && <ArrowRight className="h-4 w-4 ml-1" />}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-slate-700">Seed Operator Codes</CardTitle>
            <CardDescription className="text-xs text-slate-500">The SQL seed currently creates these example operator codes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-slate-600">
            <div className="rounded-3 border px-3 py-2 d-flex justify-content-between"><span>HR</span><strong>M001</strong></div>
            <div className="rounded-3 border px-3 py-2 d-flex justify-content-between"><span>Director</span><strong>M002</strong></div>
            <div className="rounded-3 border px-3 py-2 d-flex justify-content-between"><span>RM</span><strong>M003</strong></div>
            <div className="text-xs text-slate-400 pt-1">
              You can change or add your own operators in `recruit_operators` on Supabase anytime.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
